import { FC, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { SheetProductForm } from './ProductFormSheet';
import { ProductFormValues } from '@libs/schemas';
import { trpc } from 'src/trpc';
import { useToast } from 'components/useToast';

interface NewProductSheetProps {
  onSuccess?: () => void;
}

export const NewProductSheet: FC<NewProductSheetProps> = ({ onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { successToast, errorToast } = useToast();

  const createProductMutation = trpc.admin.product.createProduct.useMutation({
    onSuccess: () => {
      successToast({
        title: 'Product created successfully',
      });
      setOpen(false);
      onSuccess?.();
    },
    onError: (error) => {
      errorToast({
        title: 'Failed to create product',
        description: error.message || 'Failed to create product',
      });
    },
  });

  const handleSubmit = async (data: ProductFormValues) => {
    setIsLoading(true);
    try {
      await createProductMutation.mutateAsync({
        ...data,
        priceInCents: (Number(data.priceInCents) * 100).toString(),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button>New Product</Button>
      </SheetTrigger>
      <SheetContent className="p-0 sm:max-w-[600px] lg:max-w-[700px] xl:max-w-[800px] h-full">
        <div className="grid h-full grid-rows-[auto_1fr_auto]">
          <SheetHeader className="border-b bg-white px-6 py-4">
            <SheetTitle className="text-left text-lg font-semibold text-text-primary">
              Create New Product
            </SheetTitle>
          </SheetHeader>

          <SheetProductForm onSubmit={handleSubmit} isLoading={isLoading} />
        </div>
      </SheetContent>
    </Sheet>
  );
};
